import React from 'react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getImageUrl } from '../../../utils/images';


const AUTO_MS = 6000;
const RESUME_DELAY_MS = 700;

const MOMENTUM_PROJECT_MS = 220;
const MOMENTUM_MAX_EXTRA_SLIDES = 2;

const LOOP = true;
const RUBBER_BAND = true;


const SocialIcon = ({ kind }) => {
  const text = kind === 'in' ? 'in' : kind === 'p' ? 'p' : 'g+';
  return <span className={`testimonials__socialIcon testimonials__socialIcon--${kind}`}>{text}</span>;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const mod = (n, m) => ((n % m) + m) % m;
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const rubberBand = (dx, width) => {
  const w = Math.max(1, width);
  const abs = Math.abs(dx);
  const sign = dx < 0 ? -1 : 1;
  const capped = (abs * 0.55 * w) / (abs + 0.55 * w);
  return sign * capped;
};



const TestimonialsView = ({ data = {} })  => {

  const {
    title = '',
    slides = [],
  } = data;

  const [active, setActive] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false);

  const total = slides.length;
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const dragRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    deltaX: 0,
    isDragging: false,
    hasLockedAxis: false,
    isHorizontal: false,
    lastX: 0,
    lastT: 0,
    velocityPxPerMs: 0,
  });

  const resumeTimerRef = useRef<number | null>(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }, []);

  const goTo = useCallback(
    (idx) => {
      if (total <= 0) return;
      setActive(() => (LOOP ? mod(idx, total) : clamp(idx, 0, total - 1)));
    },
    [total],
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);
  const isPaused = isHovered || isFocused || isDragging || isPointerDown;

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (isPaused) return;
    const id = window.setInterval(() => goTo(active + 1), AUTO_MS);
    return () => window.clearInterval(id);
  }, [isPaused, goTo, prefersReducedMotion, active]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    },
    [prev, next],
  );

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current != null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      const el = viewportRef.current;
      if (!el) return;

      const target = e.target;
      const interactive = target?.closest?.(
        'a,button,input,textarea,select,option,[role="button"],[data-no-drag]',
      );
      if (interactive) {
        return;
      }

      clearResumeTimer();

      const t = nowMs();

      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        deltaX: 0,
        isDragging: true,
        hasLockedAxis: false,
        isHorizontal: false,
        lastX: e.clientX,
        lastT: t,
        velocityPxPerMs: 0,
      };

      setIsPointerDown(true);
      setIsDragging(true);
      setDragOffsetPx(0);

      el.setPointerCapture(e.pointerId);
    },
    [clearResumeTimer],
  );

  const onPointerMove = useCallback(
    (e) => {
      const st = dragRef.current;
      if (!st.isDragging || st.pointerId !== e.pointerId) return;

      const dxRaw = e.clientX - st.startX;
      const dy = e.clientY - st.startY;

      if (!st.hasLockedAxis) {
        const dist = Math.hypot(dxRaw, dy);
        if (dist < 6) return;
        st.hasLockedAxis = true;
        st.isHorizontal = Math.abs(dxRaw) >= Math.abs(dy);
      }

      if (!st.isHorizontal) return;

      const t = nowMs();
      const dt = Math.max(1, t - st.lastT);
      st.velocityPxPerMs = (e.clientX - st.lastX) / dt;
      st.lastX = e.clientX;
      st.lastT = t;

      const width = viewportRef.current?.getBoundingClientRect().width ?? 1;

      let dx = dxRaw;
      if (!LOOP && RUBBER_BAND) {
        const atFirst = active === 0;
        const atLast = active === total - 1;
        if ((atFirst && dxRaw > 0) || (atLast && dxRaw < 0)) {
          dx = rubberBand(dxRaw, width);
        }
      }

      st.deltaX = dx;
      setDragOffsetPx(dx);
    },
    [active, total],
  );

  const computeMomentumTarget = useCallback(
    (dx, width, velocityPxPerMs) => {
      const projectedDx = dx + velocityPxPerMs * MOMENTUM_PROJECT_MS;

      const progressSlides = -projectedDx / Math.max(1, width);
      let deltaSlides = Math.round(progressSlides);

      deltaSlides = clamp(deltaSlides, -MOMENTUM_MAX_EXTRA_SLIDES, MOMENTUM_MAX_EXTRA_SLIDES);

      const tinyMove = Math.abs(dx) < Math.max(40, width * 0.08);
      const tinyVel = Math.abs(velocityPxPerMs) < 0.15;
      if (tinyMove && tinyVel) return active;

      const target = active + deltaSlides;
      return LOOP ? mod(target, total) : clamp(target, 0, total - 1);
    },
    [active, total],
  );

  const finishDrag = useCallback(
    (pointerId) => {
      const st = dragRef.current;
      if (!st.isDragging || st.pointerId !== pointerId) return;

      const width = viewportRef.current?.getBoundingClientRect().width ?? 1;

      const dx = st.deltaX;
      const v = st.velocityPxPerMs;

      st.isDragging = false;
      st.pointerId = null;

      setIsDragging(false);
      setIsPointerDown(false);
      setDragOffsetPx(0);

      if (st.isHorizontal) {
        const target = computeMomentumTarget(dx, width, v);
        goTo(target);
      }

      clearResumeTimer();
      resumeTimerRef.current = window.setTimeout(() => {
        resumeTimerRef.current = null;
      }, RESUME_DELAY_MS);
    },
    [computeMomentumTarget, goTo, clearResumeTimer],
  );

  const onPointerUp = useCallback(
    (e) => finishDrag(e.pointerId),
    [finishDrag],
  );

  const onPointerCancel = useCallback(
    (e) => finishDrag(e.pointerId),
    [finishDrag],
  );

  const onTouchStart = useCallback(() => {
    clearResumeTimer();
    setIsPointerDown(true);
  }, [clearResumeTimer]);

  const onTouchEnd = useCallback(() => {
    setIsPointerDown(false);
  }, []);

  useEffect(() => {
    return () => clearResumeTimer();
  }, [clearResumeTimer]);

  const trackStyle = {
    transform: `translateX(calc(-${active * 100}% + ${dragOffsetPx}px))`,
  };

  const current = slides?.[active] ?? null;
  const currentImageUrl = getImageUrl(current?.image);

  const SOCIAL_LABELS = {
    in: 'LinkedIn',
    p: 'Pinterest',
    g: 'Google Plus',
  };

  return (
    <section className="testimonials">
      {slides?.length ? (
        <div className="testimonials__container">
          <header className="testimonials__heading">
            <h2 className="testimonials__title">{title}</h2>
            <div className="testimonials__titleRule" />
          </header>

          <div className="testimonials__cardWrap">
            <div className="testimonials__thumb" aria-hidden="true">
              <img
                key={`${currentImageUrl}-${current.name}-${active}`}
                className="testimonials__thumbImg"
                src={currentImageUrl}
                alt=""
              />
            </div>

            <div
              ref={viewportRef}
              className={`testimonials__viewport ${isDragging ? 'is-dragging' : ''}`}
              role="region"
              aria-roledescription="carousel"
              aria-label="Testimonials"
              tabIndex={0}
              onKeyDown={onKeyDown}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              onTouchCancel={onTouchEnd}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              <div className="testimonials__track" style={trackStyle}>
                {slides.map((t, i) => (
                  <div
                    key={`${t.name}-${i}`}
                    className="testimonials__slide"
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${i + 1} of ${total}`}
                  >
                    <div className="testimonials__card">
                      <div className="testimonials__meta">
                        <div className="testimonials__name">{t.name}</div>
                        <div className="testimonials__role">{t.role}</div>

                        {t.socials?.length ? (
                          <div className="testimonials__socials" aria-label="Social links">
                            {t.socials?.map((s, idx) => (
                              <a
                                key={`${s.social}-${idx}`}
                                className="testimonials__socialLink"
                                href={s.link}
                                aria-label={SOCIAL_LABELS[s.social] || s.social}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-no-drag
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <SocialIcon kind={s.social} />
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </div>

                      <div className="testimonials__quote">
                        <span className="testimonials__quoteMark" aria-hidden="true">
                          “
                        </span>
                        <p className="testimonials__quoteText">{t.quote}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="testimonials__dots" role="tablist" aria-label="Testimonial slides">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`testimonials__dot ${i === active ? 'is-active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1} of ${total}`}
                aria-selected={i === active}
                role="tab"
              />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default TestimonialsView;